/**
 * PactDoorVFXHandler — Pact Door's doors (DJ decision D24, plan 04a §4.1).
 *
 * `vfx:pact_door` {action:'open'} stands a door frame of fel fire on the
 * given feet point: the frame rises out of the ground, its leaf swings open
 * past edge-on, the ember lantern over the lintel flickers; after `holdMs`
 * (or on {action:'close'}) the leaf swings shut and the door fades away.
 * The origin door stays open for the whole return window; the far door
 * shuts soon after the warlock steps out.
 *
 * Render cost (docs/guides/PERF_ANALYSIS.md, plan 16): every door is a
 * pooled rig of five graphics drawn ONCE, in local coordinates, when the rig
 * is first made. Opening, holding and closing only write position, alpha and
 * scale (TweenHost); a free rig is parked at alpha 0. Nothing redraws a
 * graphics or writes `visible` after creation, so a reused door causes no
 * stage rebuild. At most PACT_DOOR_LOOK.poolSize rigs exist; a busier moment
 * recycles the oldest open door.
 */
import { createGraphics } from '../../rendering/utils/Graphics';
import type { IGraphics } from '../../rendering/IRenderer';
import { TweenHost, type TweenHostHandle } from '../../rendering/TweenHost';
import { Easing } from '../../rendering/Tween';
import { EVENTS } from '../../../shared/EventBus';
import { PACT_DOOR_LOOK as K } from '../../../config/effects/pactDoorLook.config';

type Part = IGraphics & { alpha: number; scaleX: number; scaleY: number };

interface Rig {
  halo: Part;
  doorway: Part;
  leaf: Part;
  frame: Part;
  lantern: Part;
  /** The door this rig shows; null = parked, free for reuse. */
  doorId: string | null;
  /** +1: hinge on the left post, leaf opening toward +x; -1 mirrored. */
  sign: 1 | -1;
  closing: boolean;
  /** Open order, to recycle the oldest when the pool is full. */
  openedSeq: number;
  /** Bumped on every open/park so a stale chained callback does nothing. */
  gen: number;
  tweens: TweenHostHandle[];
  clock: { t: number };
}

export interface PactDoorEvent {
  doorId: string;
  action: 'open' | 'close';
  x?: number;
  y?: number;
  flipX?: boolean;
  holdMs?: number;
}

const W = K.width;
const H = K.height;
const P = K.post;
const L = K.lintel;
const C = K.colors;
/** The leaf fills the doorway under the lintel. */
const LEAF_W = W - 2 * P;
const LEAF_H = H - L;

function part(depth: number, glow = false): Part {
  return createGraphics(null, { depth, glow }) as Part;
}

function drawHalo(g: Part): void {
  g.fillRect(-W / 2 - 3, -H - 3, P + 6, H + 3, C.fel, 0.55);
  g.fillRect(W / 2 - P - 3, -H - 3, P + 6, H + 3, C.fel, 0.55);
  g.fillRect(-W / 2 - 7, -H - 3, W + 14, L + 6, C.fel, 0.55);
}

function drawDoorway(g: Part): void {
  if (g.fillVerticalGradientRect) g.fillVerticalGradientRect(-W / 2 + P, -LEAF_H, LEAF_W, LEAF_H, C.voidTop, C.voidBottom, 0.95);
  else g.fillRect(-W / 2 + P, -LEAF_H, LEAF_W, LEAF_H, C.voidBottom, 0.95);
  g.fillRect(-W / 2 + P, -3, LEAF_W, 3, C.fel, 0.55); // light spilling over the sill
}

/** The leaf, hinged at the local origin and reaching toward +x. */
function drawLeaf(g: Part): void {
  g.fillRect(0, -LEAF_H, LEAF_W, LEAF_H, C.leaf, 1);
  const panelH = LEAF_H / 2 - 9;
  g.fillRect(5, -LEAF_H + 6, LEAF_W - 10, panelH, C.leafPanel, 0.85);
  g.fillRect(5, -LEAF_H / 2 + 3, LEAF_W - 10, panelH, C.leafPanel, 0.85);
  g.strokeCircle(LEAF_W / 2, -LEAF_H / 2, 6, C.rune, 1.5, 0.9); // the pact sigil
  g.strokeLine(LEAF_W / 2, -LEAF_H / 2 - 6, LEAF_W / 2, -LEAF_H / 2 + 6, C.rune, 1.5, 0.9);
  g.strokeCircle(LEAF_W - 7, -LEAF_H * 0.45, 3, C.ember, 2, 1); // knocker ring
  g.fillRect(LEAF_W - 2, -LEAF_H, 2, LEAF_H, C.crimson, 0.9); // the free edge catches the light
}

function drawFrame(g: Part): void {
  g.fillPolygon([{ x: -W / 2 - 4, y: -H }, { x: 0, y: -H - 12 }, { x: W / 2 + 4, y: -H }], C.felDeep, 1);
  g.fillRect(-W / 2, -H, P, H, C.fel, 1);
  g.fillRect(W / 2 - P, -H, P, H, C.fel, 1);
  g.fillRect(-W / 2 + 2, -H + L, 2, H - L, C.felDeep, 1);
  g.fillRect(W / 2 - 4, -H + L, 2, H - L, C.felDeep, 1);
  g.fillRect(-W / 2 - 4, -H, W + 8, L, C.fel, 1);
  g.fillRect(-W / 2 + 1, -H * 0.6, 4, 3, C.ember, 1); // rune notches
  g.fillRect(W / 2 - 5, -H * 0.6, 4, 3, C.ember, 1);
  g.fillRect(-W / 2 - 5, -2, W + 10, 4, C.fel, 1); // threshold
}

function drawLantern(g: Part): void {
  g.fillCircle(0, -H - 18, 9, C.ember, 0.45);
  g.fillCircle(0, -H - 18, 4.5, C.emberCore, 1);
}

export class PactDoorVFXHandler {
  private rigs: Rig[] = [];
  private seq = 0;

  /** Install subscriptions via the owner's tracked `sub` helper. */
  register(sub: (event: string, handler: (data: any) => void) => void): void {
    sub(EVENTS.VFX_PACT_DOOR, (data: PactDoorEvent) => this.onDoor(data));
    // An in-place map switch leaves no door standing on the new map.
    sub(EVENTS.MAP_SERVER_SWITCH_START, () => this.parkAll());
  }

  onDoor(data: PactDoorEvent): void {
    if (!data?.doorId) return;
    if (data.action === 'close') {
      const rig = this.rigs.find((r) => r.doorId === data.doorId);
      if (rig) this.close(rig);
      return;
    }
    if (typeof data.x !== 'number' || typeof data.y !== 'number') return;
    this.open(data.doorId, data.x, data.y, data.flipX === true, data.holdMs ?? K.exitHoldMs);
  }

  /** Graphics made so far (diagnostics and the pool test). */
  rigCount(): number {
    return this.rigs.length;
  }

  openDoorIds(): string[] {
    return this.rigs.filter((r) => r.doorId !== null).map((r) => r.doorId as string);
  }

  parkAll(): void {
    for (const rig of this.rigs) this.park(rig);
  }

  private make(): Rig {
    const rig: Rig = {
      halo: part(K.depth.halo, true),
      doorway: part(K.depth.void),
      leaf: part(K.depth.leaf),
      frame: part(K.depth.frame),
      lantern: part(K.depth.lantern, true),
      doorId: null, sign: 1, closing: false, openedSeq: 0, gen: 0, tweens: [], clock: { t: 0 },
    };
    drawHalo(rig.halo);
    drawDoorway(rig.doorway);
    drawLeaf(rig.leaf);
    drawFrame(rig.frame);
    drawLantern(rig.lantern);
    this.rigs.push(rig);
    this.park(rig);
    return rig;
  }

  private parts(rig: Rig): Part[] {
    return [rig.halo, rig.doorway, rig.leaf, rig.frame, rig.lantern];
  }

  private park(rig: Rig): void {
    for (const t of rig.tweens) t.kill();
    rig.tweens = [];
    rig.gen++;
    rig.doorId = null;
    rig.closing = false;
    for (const p of this.parts(rig)) {
      p.setAlpha(0);
      p.setScale(1, 1);
    }
  }

  private acquire(): Rig {
    const free = this.rigs.find((r) => r.doorId === null);
    if (free) return free;
    if (this.rigs.length < K.poolSize) return this.make();
    const oldest = this.rigs.reduce((a, b) => (b.openedSeq < a.openedSeq ? b : a));
    this.park(oldest);
    return oldest;
  }

  private tween(rig: Rig, target: object, to: Record<string, number>, durationMs: number,
    opts: { ease?: (t: number) => number; yoyo?: boolean; repeat?: number; then?: () => void } = {}): void {
    const gen = rig.gen;
    rig.tweens.push(TweenHost.add(target, {
      to, durationMs, ease: opts.ease, yoyo: opts.yoyo, repeat: opts.repeat,
      onComplete: opts.then ? () => { if (rig.gen === gen) opts.then!(); } : undefined,
    }));
  }

  private open(doorId: string, x: number, y: number, flipX: boolean, holdMs: number): void {
    const same = this.rigs.find((r) => r.doorId === doorId);
    if (same) this.park(same);
    const rig = this.acquire();
    rig.gen++;
    rig.doorId = doorId;
    rig.closing = false;
    rig.sign = flipX ? -1 : 1;
    rig.openedSeq = ++this.seq;
    const hingeX = rig.sign > 0 ? -W / 2 + P : W / 2 - P;
    for (const p of [rig.halo, rig.doorway, rig.frame, rig.lantern]) p.setPosition(x, y);
    rig.leaf.setPosition(x + hingeX, y);

    // Rise: frame, halo and doorway grow up out of the ground (origin = feet).
    for (const p of [rig.halo, rig.frame, rig.doorway]) p.setScale(1, 0.3);
    rig.leaf.setScale(rig.sign, 1);
    const risen = { alpha: 1, scaleY: 1 };
    this.tween(rig, rig.frame, risen, K.riseMs, { ease: Easing.power2Out, then: () => this.swingOpen(rig) });
    this.tween(rig, rig.halo, risen, K.riseMs, { ease: Easing.power2Out });
    this.tween(rig, rig.doorway, { alpha: 0.95, scaleY: 1 }, K.riseMs, { ease: Easing.power2Out });
    this.tween(rig, rig.leaf, { alpha: 1 }, K.riseMs);
    rig.clock.t = 0;
    this.tween(rig, rig.clock, { t: 1 }, Math.max(holdMs, K.riseMs + K.swingOpenMs), { then: () => this.close(rig) });
  }

  private swingOpen(rig: Rig): void {
    this.tween(rig, rig.leaf, { scaleX: rig.sign * K.openLeafScale }, K.swingOpenMs, { ease: Easing.cubicOut });
    this.tween(rig, rig.lantern, { alpha: 1 }, 200, {
      then: () => this.tween(rig, rig.lantern, { alpha: K.flickerAlpha }, K.flickerMs,
        { ease: Easing.sineInOut, yoyo: true, repeat: -1 }),
    });
  }

  private close(rig: Rig): void {
    if (rig.doorId === null || rig.closing) return;
    for (const t of rig.tweens) t.kill();
    rig.tweens = [];
    rig.closing = true;
    // The leaf swings shut, then the door sinks and fades.
    this.tween(rig, rig.leaf, { scaleX: rig.sign, alpha: 1 }, K.swingShutMs, {
      ease: Easing.power2,
      then: () => {
        const gone = { alpha: 0, scaleY: 0.5 };
        for (const p of [rig.halo, rig.doorway, rig.leaf]) this.tween(rig, p, gone, K.fadeMs, { ease: Easing.power2 });
        this.tween(rig, rig.lantern, { alpha: 0 }, K.fadeMs);
        this.tween(rig, rig.frame, gone, K.fadeMs, { ease: Easing.power2, then: () => this.park(rig) });
      },
    });
  }
}
